import moment from 'moment-timezone';
import { DateItinerary } from 'models/itinerary';
import { DigitalMapPin } from 'models/digitalMap';
import { DigitalMap } from 'models/digitalMap';
import { findActivePinSchedule } from 'components/Map/util';

export const getWalkingTimesByPinKey = async (
  digitalMap: DigitalMap,
  pinKeys: string[]
): Promise<Record<string, string>> => {
  const walkingTimesByPinKey: Record<string, string> = {};

  if (pinKeys.length > 1) {
    // Get coordinates for all pins in sequence
    const waypoints = pinKeys
      .map((key) => {
        const pin = digitalMap.pins.find((p) => p.key === key);
        return pin ? { lat: pin.location.latitude, lng: pin.location.longitude } : null;
      })
      .filter(Boolean);

    if (waypoints.length > 1) {
      const directionsService = new google.maps.DirectionsService();

      const request = {
        origin: waypoints[0],
        destination: waypoints[waypoints.length - 1],
        waypoints: waypoints.slice(1, -1).map((point) => ({
          location: point,
          stopover: true,
        })),
        travelMode: google.maps.TravelMode.WALKING,
        optimizeWaypoints: false,
      };

      try {
        const result = await directionsService.route(request as google.maps.DirectionsRequest);

        // Extract walking times between consecutive points
        if (result.routes[0]?.legs) {
          result.routes[0].legs.forEach((leg, legIndex) => {
            const minutes = Math.round((leg.duration?.value ?? 0) / 60);
            walkingTimesByPinKey[pinKeys[legIndex + 1]] = `${minutes}m`;
          });
        }
      } catch (error) {
        console.error('Error getting directions:', error);
        // Fallback walking times if directions fail
        for (let i = 0; i < pinKeys.length - 1; i++) {
          walkingTimesByPinKey[pinKeys[i + 1]] = '-';
        }
      }
    }
  }

  return walkingTimesByPinKey;
};

export const generateDateItineraries = async (
  digitalMap: DigitalMap,
  startDate: string,
  endDate: string,
  pinKeys: string[]
): Promise<DateItinerary[]> => {
  const dates: string[] = [];
  const currentDate = new Date(startDate);
  const end = new Date(endDate);

  while (currentDate <= end) {
    dates.push(currentDate.toISOString().split('T')[0]);
    currentDate.setDate(currentDate.getDate() + 1);
  }

  const walkingTimesByPinKey = await getWalkingTimesByPinKey(digitalMap, pinKeys);

  const pinsPerDay = Math.ceil(pinKeys.length / dates.length);
  const dateItineraries = dates
    .map((date, index) => {
      const startIndex = index * pinsPerDay;
      const endIndex = Math.min(startIndex + pinsPerDay, pinKeys.length);
      const dayPins = pinKeys.slice(startIndex, endIndex);

      return {
        date,
        items: dayPins.map((pinKey, itemIndex) => {
          const walkingTime = itemIndex === 0 ? '-' : walkingTimesByPinKey[pinKey];

          return {
            time: '',
            pin_key: pinKey,
            duration: '',
            travel_time: walkingTime,
          };
        }),
      };
    })
    .filter((dateItinerary) => dateItinerary.items.length > 0);

  return dateItineraries;
};

export const getNextAvailableTime = (
  digitalMapPin: DigitalMapPin | undefined,
  date: string,
  initialTime: string,
  latestTime?: string
): string | undefined => {
  if (!digitalMapPin) {
    return initialTime;
  }

  if (digitalMapPin.time_data_type === 'TIME_RANGE' && digitalMapPin.open_hour_schedules?.length) {
    const activeSchedule = findActivePinSchedule(
      digitalMapPin.open_hour_schedules,
      moment(date, 'YYYY-MM-DD')
    );
    if (!activeSchedule) {
      return undefined;
    }

    const startTime = moment(activeSchedule.start_time_local, 'HH:mm');
    const endTime = moment(activeSchedule.end_time_local, 'HH:mm');
    const currentTime = moment(initialTime, 'HH:mm');
    const maxTime = latestTime ? moment(latestTime, 'HH:mm') : undefined;

    // Check if the time would be after latestTime
    if (maxTime && currentTime.isAfter(maxTime)) {
      return undefined;
    }

    if (currentTime.isSameOrAfter(startTime) && currentTime.isSameOrBefore(endTime)) {
      return currentTime.format('HH:mm');
    } else if (currentTime.isBefore(startTime)) {
      // Check if opening time would be after latestTime
      if (maxTime && startTime.isAfter(maxTime)) {
        return undefined;
      }
      return startTime.format('HH:mm');
    }

    return undefined;
  } else if (digitalMapPin.time_data_type === 'SHOWTIMES') {
    const activeSchedule = findActivePinSchedule(
      digitalMapPin.showtime_schedules,
      moment(date, 'YYYY-MM-DD')
    );
    if (!activeSchedule) {
      return undefined;
    }

    const maxTime = latestTime ? moment(latestTime, 'HH:mm') : undefined;

    return activeSchedule.showtimes?.find((showtime) => {
      const showtimeMoment = moment(showtime, 'HH:mm');
      return (
        showtimeMoment.isSameOrAfter(moment(initialTime, 'HH:mm')) &&
        (!maxTime || showtimeMoment.isSameOrBefore(maxTime))
      );
    });
  }

  return initialTime;
};

export const getTimeOptions = (
  digitalMapPin: DigitalMapPin | undefined,
  date: string
): string[] => {
  if (!digitalMapPin) {
    return [];
  }

  if (digitalMapPin.time_data_type === 'TIME_RANGE' && digitalMapPin.open_hour_schedules?.length) {
    const activeSchedule = findActivePinSchedule(
      digitalMapPin.open_hour_schedules,
      moment(date, 'YYYY-MM-DD')
    );
    if (!activeSchedule) {
      return [];
    }

    const startTime = moment(activeSchedule.start_time_local, 'HH:mm');
    const endTime = moment(activeSchedule.end_time_local, 'HH:mm');
    const options: string[] = [];

    // Use the pin's operating hours directly
    const currentTime = startTime.clone();

    // Generate times in 30-minute intervals until closing
    while (currentTime.isSameOrBefore(endTime)) {
      options.push(currentTime.format('HH:mm'));
      currentTime.add(30, 'minutes');
    }

    return options;
  } else if (digitalMapPin.time_data_type === 'SHOWTIMES') {
    const activeSchedule = findActivePinSchedule(
      digitalMapPin.showtime_schedules,
      moment(date, 'YYYY-MM-DD')
    );
    if (!activeSchedule) {
      return [];
    }

    // Use all showtimes without filtering
    return (activeSchedule.showtimes || []).sort();
  }

  // If no time constraints, return default 30-minute intervals from 9am to 6pm
  const defaultOptions: string[] = [];
  const currentTime = moment('09:00', 'HH:mm');
  const endTime = moment('18:00', 'HH:mm');

  while (currentTime.isSameOrBefore(endTime)) {
    defaultOptions.push(currentTime.format('HH:mm'));
    currentTime.add(30, 'minutes');
  }

  return defaultOptions;
};
